/**
 * createBulkTableHook — Factory for LiveView bulk-edit table hooks.
 *
 * Provides shared behavior for inline-editable tables:
 * - Element caching (single DOM traversal on mount)
 * - Dirty tracking (initial vs current values per row)
 * - "Apply defaults" (copy default row values to data rows)
 * - Save with confirmation modal, "Saving..." state, submitted-row tracking
 * - Error display / clear
 * - Filter bar (all / empty / populated) + seafarer name search
 * - Reset all dirty rows to initial values
 *
 * Extension points (passed via `options`):
 * - `buildRowCacheExtras(tr)` — return extra fields to merge into the row cache entry
 * - `onRowChange(rowId)` — called after dirty check on input/change (e.g. recalculate)
 * - `isRowValid(rowData)` — whether a dirty row has enough data to include in save
 * - `onInit()` — called at the end of mounted(), after all base setup is complete
 *
 * Designed for 2000+ row tables.
 */

export interface RowData {
  [field: string]: string;
}

export interface RowCache {
  tr: HTMLElement;
  inputs: Map<string, HTMLInputElement | HTMLSelectElement>;
  seafarerName: string;
  [extra: string]: unknown;
}

export interface BulkTableOptions {
  /** Return extra fields to merge into each row's cache entry. */
  buildRowCacheExtras?: (tr: HTMLElement) => Record<string, unknown>;
  /** Called after a row's dirty state is checked (input/change events). */
  onRowChange?: (this: any, rowId: string) => void;
  /** Determines if a dirty row should be included when saving. Default: any non-empty field. */
  isRowValid?: (rowData: RowData) => boolean;
  /** Called at the end of mounted(), after all base setup. */
  onInit?: (this: any) => void;
  /**
   * Additional filter tags rendered as buttons in the filter bar.
   * Rows are matched by their `data-filter-tag` attribute.
   * Tag labels are humanized (underscores → spaces, first letter capitalised).
   */
  filterTags?: string[];
  /** Build the confirmation message shown before saving. Receives dirty items and total count. */
  buildConfirmMessage?: (
    items: Record<string, RowData>,
    count: number,
  ) => string;
  /** Called after save_success, with the full server payload. */
  onAfterSaveSuccess?: (this: any, payload: Record<string, any>) => void;
}

function defaultIsRowValid(rowData: RowData): boolean {
  return Object.values(rowData).some((v) => v !== "");
}

function humanizeTag(tag: string): string {
  return tag
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function getInputValue(input: HTMLInputElement | HTMLSelectElement): string {
  if (input instanceof HTMLInputElement && input.type === "checkbox") {
    return input.checked ? "true" : "false";
  }
  return input.value;
}

function setInputValue(
  input: HTMLInputElement | HTMLSelectElement,
  value: string,
): void {
  if (input instanceof HTMLInputElement && input.type === "checkbox") {
    input.checked = value === "true";
  } else {
    input.value = value;
  }
}

export default function createBulkTableHook(options: BulkTableOptions = {}) {
  const {
    buildRowCacheExtras,
    onRowChange,
    isRowValid = defaultIsRowValid,
    onInit,
    filterTags,
    buildConfirmMessage = (_items, count) =>
      `This will update ${count} payslip${count === 1 ? "" : "s"}. Do you want to continue?`,
    onAfterSaveSuccess,
  } = options;

  return {
    mounted(this: any) {
      this.phxTarget = this.el.dataset.phxTarget;
      this.initialValues = new Map<string, RowData>();
      this.dirtyRows = new Set<string>();
      this.rowCache = new Map<string, RowCache>();
      this.saveBtn = this.el.querySelector("[data-action='save-all']");

      this.buildCache();
      this.storeInitialValues();
      this.attachEventListeners();
      this.attachFilterListeners();

      this.handleEvent("save_success", (payload: any) => {
        this.saving = false;
        this.onSaveSuccess();
        if (onAfterSaveSuccess) onAfterSaveSuccess.call(this, payload ?? {});
      });
      this.handleEvent("save_errors", (payload: any) => {
        this.saving = false;
        this.displayErrors(payload.errors);
      });

      if (onInit) onInit.call(this);
    },

    buildCache(this: any) {
      const allInputs = this.el.querySelectorAll("[data-row-id][data-field]");
      allInputs.forEach((input: HTMLInputElement | HTMLSelectElement) => {
        const rowId = input.dataset.rowId!;
        const field = input.dataset.field!;

        if (!this.rowCache.has(rowId)) {
          const tr = input.closest("tr") as HTMLElement;
          const extras = buildRowCacheExtras ? buildRowCacheExtras(tr) : {};
          this.rowCache.set(rowId, {
            tr,
            inputs: new Map(),
            seafarerName: (tr?.querySelector("td")?.textContent || "")
              .trim()
              .toLowerCase(),
            ...extras,
          } as RowCache);
        }

        this.rowCache.get(rowId)!.inputs.set(field, input);
      });
    },

    storeInitialValues(this: any) {
      this.rowCache.forEach((cache: RowCache, rowId: string) => {
        const rowData: RowData = {};
        cache.inputs.forEach((input, field) => {
          rowData[field] = getInputValue(input);
        });
        this.initialValues.set(rowId, rowData);
      });
    },

    attachEventListeners(this: any) {
      this.el.addEventListener("input", (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (!target.dataset?.rowId) return;
        this.checkDirtyRow(target.dataset.rowId);
        if (onRowChange) onRowChange.call(this, target.dataset.rowId);
      });

      this.el.addEventListener("change", (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (!target.dataset?.rowId) return;
        this.checkDirtyRow(target.dataset.rowId);
        if (onRowChange) onRowChange.call(this, target.dataset.rowId);
      });

      const applyBtn = this.el.querySelector("[data-action='apply-defaults']");
      if (applyBtn) {
        applyBtn.addEventListener("click", () => this.applyDefaults(false));
      }

      const applyAllBtn = this.el.querySelector(
        "[data-action='apply-defaults-all']",
      );
      if (applyAllBtn) {
        applyAllBtn.addEventListener("click", () => this.applyDefaults(true));
      }

      if (this.saveBtn) {
        this.saveBtn.addEventListener("click", () => this.saveAll());
      }

      const clearBtn = this.el.querySelector("[data-action='clear-all']");
      if (clearBtn) {
        clearBtn.addEventListener("click", () => this.resetAll());
      }
    },

    checkDirtyRow(this: any, rowId: string) {
      const cache: RowCache | undefined = this.rowCache.get(rowId);
      if (!cache) return;

      const initial = this.initialValues.get(rowId) || {};
      let isDirty = false;

      cache.inputs.forEach((input, field) => {
        if (input.disabled) return;
        if (getInputValue(input) !== (initial[field] || "")) {
          isDirty = true;
        }
      });

      if (isDirty) {
        this.dirtyRows.add(rowId);
        cache.tr?.classList.add("bulk-row-dirty");
        cache.tr?.classList.remove("bulk-row-saved");
      } else {
        this.dirtyRows.delete(rowId);
        cache.tr?.classList.remove("bulk-row-dirty");
        if (initial && Object.values(initial).some((v) => v !== "")) {
          cache.tr?.classList.add("bulk-row-saved");
        }
      }

      this.updateSaveButtonState();
    },

    applyDefaults(this: any, overrideExisting: boolean) {
      const defaultInputs = this.el.querySelectorAll(
        "[data-defaults-field]",
      ) as NodeListOf<HTMLInputElement | HTMLSelectElement>;

      const defaults: RowData = {};
      defaultInputs.forEach((input: HTMLInputElement | HTMLSelectElement) => {
        if (input.value) {
          defaults[input.dataset.defaultsField!] = input.value;
        }
      });

      if (Object.keys(defaults).length === 0) return;

      // Phase 1: Set all values (pure data mutation, minimal layout thrash)
      const affectedRowIds: string[] = [];

      this.rowCache.forEach((cache: RowCache, rowId: string) => {
        const isExisting = cache.tr?.classList.contains("bulk-row-saved");
        if (!overrideExisting && isExisting) return;

        let changed = false;
        for (const [field, value] of Object.entries(defaults)) {
          const input = cache.inputs.get(field);
          if (!input) continue;
          if (overrideExisting || !input.value) {
            input.value = value;
            changed = true;
          }
        }

        if (changed) affectedRowIds.push(rowId);
      });

      // Phase 2: Batch dirty checks + optional recalculations (deferred to next frame)
      requestAnimationFrame(() => {
        for (const rowId of affectedRowIds) {
          const cache: RowCache = this.rowCache.get(rowId)!;
          const initial = this.initialValues.get(rowId) || {};
          let isDirty = false;

          cache.inputs.forEach((input, field) => {
            if (getInputValue(input) !== (initial[field] || "")) isDirty = true;
          });

          if (isDirty) {
            this.dirtyRows.add(rowId);
            cache.tr?.classList.add("bulk-row-dirty");
            cache.tr?.classList.remove("bulk-row-saved");
          } else {
            this.dirtyRows.delete(rowId);
            cache.tr?.classList.remove("bulk-row-dirty");
            if (initial && Object.values(initial).some((v) => v !== "")) {
              cache.tr?.classList.add("bulk-row-saved");
            }
          }

          if (onRowChange) onRowChange.call(this, rowId);
        }

        this.updateSaveButtonState();
      });
    },

    collectDirtyRows(this: any): Record<string, RowData> {
      const items: Record<string, RowData> = {};

      this.dirtyRows.forEach((rowId: string) => {
        const cache: RowCache | undefined = this.rowCache.get(rowId);
        if (!cache) return;

        const rowData: RowData = {};
        cache.inputs.forEach((input, field) => {
          if (input.disabled) return;
          rowData[field] = getInputValue(input);
        });

        if (isRowValid(rowData)) {
          items[rowId] = rowData;
        }
      });

      return items;
    },

    saveAll(this: any) {
      this.clearErrors();

      const items = this.collectDirtyRows();
      const count = Object.keys(items).length;
      if (count === 0) return;

      const message = buildConfirmMessage(items, count);
      const modal = document.getElementById("confirmation-popup");

      if (modal) {
        modal.querySelector("[data-popup-message]")!.textContent = message;
        (window as any).liveSocket.execJS(
          modal,
          modal.getAttribute("data-exec-open"),
        );

        const onConfirm = () => {
          this.doSaveAll(items);
        };

        window.addEventListener("phx:confirm", onConfirm, { once: true });
        window.addEventListener(
          "phx:cancel",
          () => window.removeEventListener("phx:confirm", onConfirm),
          { once: true },
        );
      } else {
        this.doSaveAll(items);
      }
    },

    doSaveAll(this: any, items: Record<string, any>) {
      this.submittedRows = new Set(Object.keys(items));
      this.saving = true;
      this.updateSaveButtonState();

      if (this.phxTarget) {
        this.pushEventTo(this.phxTarget, "save_all", { items });
      } else {
        this.pushEvent("save_all", { items });
      }
    },

    onSaveSuccess(this: any) {
      const rows = this.submittedRows ?? new Set(this.dirtyRows);

      rows.forEach((rowId: string) => {
        const cache: RowCache | undefined = this.rowCache.get(rowId);
        if (!cache) return;

        const newInitial: RowData = {};
        cache.inputs.forEach((input, field) => {
          newInitial[field] = getInputValue(input);
        });
        this.initialValues.set(rowId, newInitial);

        cache.tr?.classList.remove("bulk-row-dirty");
        cache.tr?.classList.add("bulk-row-saved");
      });

      rows.forEach((rowId: string) => this.dirtyRows.delete(rowId));
      this.submittedRows = null;
      this.updateSaveButtonState();
    },

    resetAll(this: any) {
      this.clearErrors();

      // Reset defaults row inputs
      const defaultInputs = this.el.querySelectorAll(
        "[data-defaults-field]",
      ) as NodeListOf<HTMLInputElement>;
      defaultInputs.forEach((input: HTMLInputElement) => {
        input.value = "";
      });

      // Reset all dirty rows to their initial values
      this.dirtyRows.forEach((rowId: string) => {
        const cache: RowCache | undefined = this.rowCache.get(rowId);
        if (!cache) return;

        const initial = this.initialValues.get(rowId) || {};
        cache.inputs.forEach((input, field) => {
          setInputValue(input, initial[field] || "");
        });

        cache.tr?.classList.remove("bulk-row-dirty");
        if (initial && Object.values(initial).some((v) => v !== "")) {
          cache.tr?.classList.add("bulk-row-saved");
        }

        if (onRowChange) onRowChange.call(this, rowId);
      });

      this.dirtyRows.clear();
      this.updateSaveButtonState();

      window.dispatchEvent(
        new CustomEvent("phx:update-alert", {
          detail: { type: "info", message: "Data reset" },
        }),
      );
    },

    displayErrors(this: any, errors: Record<string, Record<string, string[]>>) {
      this.updateSaveButtonState();

      Object.entries(errors).forEach(([rowId, fieldErrors]) => {
        const cache: RowCache | undefined = this.rowCache.get(rowId);
        if (!cache) return;

        cache.tr?.classList.add("bulk-row-error");

        Object.entries(fieldErrors).forEach(([field, messages]) => {
          const input = cache.inputs.get(field);
          if (input) {
            input.classList.add("border-red-300", "bg-red-50");
          }

          const errorEl = this.el.querySelector(
            `[data-error-for="${rowId}-${field}"]`,
          );
          if (errorEl) {
            errorEl.textContent = (messages as string[]).join(", ");
            errorEl.classList.remove("hidden");
          }
        });
      });
    },

    clearErrors(this: any) {
      this.el
        .querySelectorAll(".bulk-row-error")
        .forEach((row: HTMLElement) => row.classList.remove("bulk-row-error"));

      this.el
        .querySelectorAll("[data-error-for]")
        .forEach((el: HTMLElement) => {
          el.textContent = "";
          el.classList.add("hidden");
        });

      this.el.querySelectorAll(".border-red-300").forEach((el: HTMLElement) => {
        el.classList.remove("border-red-300", "bg-red-50");
      });
    },

    updateSaveButtonState(this: any) {
      if (!this.saveBtn) return;

      if (this.saving) {
        this.saveBtn.disabled = true;
        this.saveBtn.textContent = "Saving...";
        this.saveBtn.classList.add(
          "disabled",
          "cursor-not-allowed",
          "opacity-50",
        );
        return;
      }

      const count = this.dirtyRows.size;
      const isDisabled = count === 0;
      this.saveBtn.disabled = isDisabled;
      this.saveBtn.textContent = count > 0 ? `Save All (${count})` : "Save All";
      this.saveBtn.classList.toggle("disabled", isDisabled);
      this.saveBtn.classList.toggle("cursor-not-allowed", isDisabled);
      this.saveBtn.classList.toggle("opacity-50", isDisabled);
    },

    attachFilterListeners(this: any) {
      const filterBar = this.el.querySelector("[data-filter-bar]");
      if (!filterBar) return;

      this.filterButtons = filterBar.querySelectorAll("[data-filter]");
      this.tagFilterButtons = filterBar.querySelectorAll("[data-filter-tag]");
      this.searchInput = filterBar.querySelector(
        "[data-search='seafarer']",
      ) as HTMLInputElement | null;
      this.activeFilter = "all";
      this.activeTagFilter = null as string | null;
      this.searchQuery = "";

      // Status filter buttons (All / Empty / Populated)
      filterBar.addEventListener("click", (e: Event) => {
        const btn = (e.target as HTMLElement).closest(
          "[data-filter]",
        ) as HTMLElement | null;
        if (!btn) return;

        const filter = btn.dataset.filter!;
        if (filter === this.activeFilter && this.activeTagFilter === null)
          return;

        this.activeFilter = filter;
        this.activeTagFilter = null;
        this.updateFilterButtonStyles();
        this.applyFilters();
      });

      // Tag filter buttons (Kadmos / Bank Transfer / MarTrust / ...)
      if (this.tagFilterButtons.length > 0) {
        filterBar.addEventListener("click", (e: Event) => {
          const btn = (e.target as HTMLElement).closest(
            "[data-filter-tag]",
          ) as HTMLElement | null;
          if (!btn) return;

          const tag = btn.dataset.filterTag!;
          if (tag === this.activeTagFilter) {
            // Toggle off — go back to "all"
            this.activeTagFilter = null;
            this.activeFilter = "all";
          } else {
            this.activeTagFilter = tag;
            this.activeFilter = "all";
          }

          this.updateFilterButtonStyles();
          this.applyFilters();
        });
      }

      if (this.searchInput) {
        this.searchInput.addEventListener("input", () => {
          this.searchQuery = this.searchInput!.value.trim().toLowerCase();
          this.applyFilters();
        });
      }
    },

    updateFilterButtonStyles(this: any) {
      const activeClass =
        "rounded-md px-3 py-1.5 text-xs font-medium text-white bg-blue-600";
      const inactiveClass =
        "rounded-md px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200";

      this.filterButtons.forEach((b: HTMLElement) => {
        b.className =
          b.dataset.filter === this.activeFilter &&
          this.activeTagFilter === null
            ? activeClass
            : inactiveClass;
      });

      this.tagFilterButtons.forEach((b: HTMLElement) => {
        b.className =
          b.dataset.filterTag === this.activeTagFilter
            ? activeClass
            : inactiveClass;
      });
    },

    applyFilters(this: any) {
      let firstVisible: HTMLElement | null = null;

      this.rowCache.forEach((cache: RowCache, _rowId: string) => {
        if (!cache.tr) return;

        const isSaved = cache.tr.classList.contains("bulk-row-saved");
        const isDirty = cache.tr.classList.contains("bulk-row-dirty");
        const isPopulated = isSaved || isDirty;

        let showByStatus = true;
        if (this.activeFilter === "empty") showByStatus = !isPopulated;
        else if (this.activeFilter === "populated") showByStatus = isPopulated;

        const showByName =
          !this.searchQuery || cache.seafarerName.includes(this.searchQuery);

        const showByTag =
          !this.activeTagFilter ||
          cache.tr.dataset.filterTag === this.activeTagFilter;

        const visible = showByStatus && showByName && showByTag;
        cache.tr.style.display = visible ? "" : "none";

        if (visible && !firstVisible) firstVisible = cache.tr;
      });

      if (this.searchQuery && firstVisible) {
        (firstVisible as HTMLElement).scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    },
  };
}
